import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import authService from '../../api-authorization/AuthorizeService';
import axios from 'axios';
import SupportUs from '../supporters/SupportUs';
import PGNViewerWithAnalysis from '../../PGNViewerWithAnalysis';
import { Button, Box, Grid, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { usePromiseTracker } from "react-promise-tracker";
import { trackPromise } from 'react-promise-tracker';
import { MagnifyingGlass } from "react-loader-spinner";
import AnalyzeOnLichess from './AnalyzeOnLichess';
import EvaluationBar from './EvaluationBar';
import { useStockfish } from '../../../hooks/useStockfish';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import { Chess } from 'chess.js';
import TopMovesPanel from './TopMovesPanel';

const extractPGNTag = (line, tagName) => {
    if (line.includes(`[${tagName} `)) {
        const match = line.match(/(?:"[^"]*"|^[^"]*$)/);
        return match ? match[0].replace(/"/g, "") : "";
    }
    return null;
};

// Tags to filter out from exported PGN (case-insensitive)
const FILTERED_PGN_TAGS = [
    'whiteuscfid',
    'whitefideid',
    'blackuscfid',
    'blackfideid'
];

const useWindowDimensions = () => {
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return dimensions;
};

const PlayerLink = ({ name, uscfId, fideId, elo }) => {
    const renderPlayerName = () => {
        if (uscfId > 0) {
            return (
                <a
                    href={`http://www.uschess.org/msa/MbrDtlMain.php?${uscfId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {name}
                </a>
            );
        }
        if (fideId > 0) {
            return (
                <a
                    href={`https://ratings.fide.com/profile/${fideId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {name}
                </a>
            );
        }
        return name;
    };

    return (
        <>
            {elo > 0 && `[${elo}] `}
            {renderPlayerName()}
        </>
    );
};

const EventLink = ({ eventName, uscfId, fideId, date }) => {
    const renderEventName = () => {
        if (uscfId > 0) {
            return (
                <Link target="_blank" to={`/Games/GamesByEventID?idType=USCF&id=${uscfId}`}>
                    {eventName}
                </Link>
            );
        }
        if (fideId > 0) {
            return (
                <Link target="_blank" to={`/Games/GamesByEventID?idType=FIDE&id=${fideId}`}>
                    {eventName}
                </Link>
            );
        }
        return eventName;
    };

    const renderDate = () => {
        if (fideId > 0) {
            return (
                <a
                    href={`https://ratings.fide.com/tournament_information.phtml?event=${fideId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    [{date}]
                </a>
            );
        }
        if (uscfId > 0) {
            return (
                <a
                    href={`http://www.uschess.org/msa/XtblMain.php?${uscfId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    [{date}]
                </a>
            );
        }
        return `[${date}]`;
    };

    return (
        <>
            {renderEventName()} {renderDate()}
        </>
    );
};

// Mobile evaluation overlay with current move + eval + next move + best move
const MobileEvaluationOverlay = ({ evaluation, isAnalyzing, boardSize, currentMoveIndex, gameMoves, bestMove, currentFen }) => {
    const getEvalText = () => {
        if (!evaluation) return isAnalyzing ? '...' : '';

        if (evaluation.type === 'mate') {
            const sign = evaluation.value > 0 ? '+' : '-';
            return `${sign}M${Math.abs(evaluation.value)}`;
        }

        const sign = evaluation.value >= 0 ? '+' : '';
        return `${sign}${evaluation.value.toFixed(2)}`;
    };

    const getEvalColor = () => {
        if (!evaluation) return 'rgba(128, 128, 128, 0.9)';

        if (evaluation.type === 'mate') {
            return evaluation.value > 0 ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)';
        }

        if (evaluation.value > 0.5) return 'rgba(255, 255, 255, 0.95)';
        if (evaluation.value < -0.5) return 'rgba(0, 0, 0, 0.95)';
        return 'rgba(128, 128, 128, 0.95)';
    };

    const getTextColor = () => {
        if (!evaluation) return '#fff';

        if (evaluation.type === 'mate') {
            return evaluation.value > 0 ? '#000' : '#fff';
        }

        if (evaluation.value > 0.5) return '#000';
        if (evaluation.value < -0.5) return '#fff';
        return '#fff';
    };

    const getCurrentMoveText = () => {
        if (currentMoveIndex === 0 || !gameMoves || gameMoves.length === 0) {
            return '';
        }

        const move = gameMoves[currentMoveIndex - 1];
        if (!move) return '';

        const moveNumber = Math.floor((currentMoveIndex - 1) / 2) + 1;
        const isWhiteMove = (currentMoveIndex - 1) % 2 === 0;

        if (isWhiteMove) {
            return `${moveNumber}. ${move.san}`;
        } else {
            return `${moveNumber}... ${move.san}`;
        }
    };

    // Get next move in the game (main line) with move number if needed
    const getNextMoveText = () => {
        if (!gameMoves || gameMoves.length === 0 || currentMoveIndex >= gameMoves.length) {
            return '';
        }

        const move = gameMoves[currentMoveIndex];
        if (!move) return '';

        // Determine if we need to show move number
        // currentMoveIndex is the index of the next move (0-based)
        const nextMoveNumber = Math.floor(currentMoveIndex / 2) + 1;
        const isNextMoveWhite = currentMoveIndex % 2 === 0;

        // Current move's move number (the one just played)
        const currentMoveNumber = Math.floor((currentMoveIndex - 1) / 2) + 1;

        // If next move is White's move, always show move number (e.g., "26. Bg6")
        // If next move is Black's move and same move number as current, don't show number
        // If next move is Black's move and different move number, show "26... Bg6"
        if (isNextMoveWhite) {
            return `${nextMoveNumber}. ${move.san}`;
        } else {
            // Black's move - only show number if it's a different move number than current
            if (nextMoveNumber !== currentMoveNumber) {
                return `${nextMoveNumber}... ${move.san}`;
            }
            return move.san;
        }
    };

    const getBestMoveSan = () => {
        if (!bestMove || !currentFen) return '';

        try {
            const chess = new Chess(currentFen);
            const from = bestMove.slice(0, 2);
            const to = bestMove.slice(2, 4);
            const promotion = bestMove.length > 4 ? bestMove.slice(4) : undefined;

            const move = chess.move({ from, to, promotion });
            if (move) {
                return move.san;
            }
        } catch (e) {
            // Silently fail
        }
        return '';
    };

    const evalText = getEvalText();
    const currentMoveText = getCurrentMoveText();
    const nextMoveText = getNextMoveText();
    const bestMoveSan = getBestMoveSan();

    // Get just the SAN part of next move for comparison with best move
    const nextMoveSanOnly = gameMoves && gameMoves[currentMoveIndex] ? gameMoves[currentMoveIndex].san : '';

    if (!evalText && !currentMoveText) return null;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: boardSize / 2,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: getEvalColor(),
                color: getTextColor(),
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                zIndex: 10,
                pointerEvents: 'none',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                {currentMoveText && (
                    <Box
                        component="span"
                        sx={{
                            color: '#4caf50',
                            fontWeight: 'bold',
                            textShadow: getTextColor() === '#000' ? 'none' : '0 1px 2px rgba(0,0,0,0.5)'
                        }}
                    >
                        {currentMoveText}
                    </Box>
                )}
                {evalText && (
                    <Box
                        component="span"
                        sx={{
                            opacity: 0.9,
                            fontSize: '10px'
                        }}
                    >
                        ({evalText})
                    </Box>
                )}
                {nextMoveText && (
                    <Box
                        component="span"
                        sx={{
                            opacity: 0.75,
                            fontWeight: 'normal'
                        }}
                    >
                        {nextMoveText}
                    </Box>
                )}
            </Box>

            {bestMoveSan && bestMoveSan !== nextMoveSanOnly && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '10px',
                    opacity: 0.85
                }}>
                    <Box component="span" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                        Best: {bestMoveSan}
                    </Box>
                    {isAnalyzing && (
                        <Box component="span" sx={{ opacity: 0.6, fontSize: '9px' }}>
                            ...
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default function GameById(props) {
    const gameId = useMemo(() =>
        props.gameId || props.match?.params?.gameId || "",
        [props.gameId, props.match?.params?.gameId]
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [gameData, setGameData] = useState({
        pgn: null,
        event: "",
        date: "",
        white: "",
        whiteElo: 0,
        whiteFideId: 0,
        whiteUscfId: 0,
        black: "",
        blackElo: 0,
        blackFideId: 0,
        blackUscfId: 0,
        result: "",
        uscfEventId: 0,
        fideEventId: 0
    });

    const [isAdmin, setIsAdmin] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [enableAnalysis, setEnableAnalysis] = useState(false);
    const [analysisDepthSetting, setAnalysisDepthSetting] = useState(25);
    const [numberOfLines, setNumberOfLines] = useState(3);
    const [currentFen, setCurrentFen] = useState(null);
    const [boardOrientation, setBoardOrientation] = useState('white');
    const [currentPath, setCurrentPath] = useState([]);
    const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
    const [variations, setVariations] = useState({});
    const [activeVariationIndex, setActiveVariationIndex] = useState(null);
    const [startingFen, setStartingFen] = useState(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailDialogText, setEmailDialogText] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [gameMoves, setGameMoves] = useState([]);

    const lastAnalyzedRef = useRef({ fen: null, depth: null });

    const displayEditButton = props.displayEditButton !== undefined ? props.displayEditButton : true;
    const { promiseInProgress } = usePromiseTracker();
    const dimensions = useWindowDimensions();

    const { isReady, evaluation, bestMove, topMoves, analysisDepth, isAnalyzing, analyzePosition, stopAnalysis, setMultiPV } = useStockfish(numberOfLines);

    const parsePGNTags = useCallback((pgnLines) => {
        const tags = {
            event: "",
            date: "",
            white: "",
            black: "",
            whiteElo: 0,
            blackElo: 0,
            result: "",
            fideEventId: 0,
            uscfEventId: 0,
            whiteUscfId: 0,
            whiteFideId: 0,
            blackUscfId: 0,
            blackFideId: 0
        };

        pgnLines.forEach((line) => {
            const eventValue = extractPGNTag(line, "Event");
            if (eventValue) tags.event = eventValue;

            const dateValue = extractPGNTag(line, "Date");
            if (dateValue) tags.date = dateValue;

            const whiteValue = extractPGNTag(line, "White");
            if (whiteValue) tags.white = whiteValue;

            const blackValue = extractPGNTag(line, "Black");
            if (blackValue) tags.black = blackValue;

            const whiteEloValue = extractPGNTag(line, "WhiteElo");
            if (whiteEloValue) tags.whiteElo = parseInt(whiteEloValue) || 0;

            const blackEloValue = extractPGNTag(line, "BlackElo");
            if (blackEloValue) tags.blackElo = parseInt(blackEloValue) || 0;

            const resultValue = extractPGNTag(line, "Result");
            if (resultValue) tags.result = resultValue;

            const fideEventIdValue = extractPGNTag(line, "FideEventId");
            if (fideEventIdValue) tags.fideEventId = parseInt(fideEventIdValue) || 0;

            const uscfEventIdValue = extractPGNTag(line, "UscfEventId");
            if (uscfEventIdValue) tags.uscfEventId = parseInt(uscfEventIdValue) || 0;

            const whiteUscfIdValue = extractPGNTag(line, "WhiteUscfId");
            if (whiteUscfIdValue) tags.whiteUscfId = parseInt(whiteUscfIdValue) || 0;

            const whiteFideIdValue = extractPGNTag(line, "WhiteFideId");
            if (whiteFideIdValue) tags.whiteFideId = parseInt(whiteFideIdValue) || 0;

            const blackUscfIdValue = extractPGNTag(line, "BlackUscfId");
            if (blackUscfIdValue) tags.blackUscfId = parseInt(blackUscfIdValue) || 0;

            const blackFideIdValue = extractPGNTag(line, "BlackFideId");
            if (blackFideIdValue) tags.blackFideId = parseInt(blackFideIdValue) || 0;
        });

        return tags;
    }, []);

    useEffect(() => {
        if (!gameId) return;

        async function fetchGame() {
            try {
                const user = await authService.getUser();
                if (user?.role?.includes("Administrators")) {
                    setIsAdmin(true);
                }

                const token = await authService.getAccessToken();
                const headers = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};

                trackPromise(
                    axios.get(`/api/chessgames/getSingleGame?gamehash=${gameId}`, headers)
                        .then((response) => {
                            const pgnLines = response.data;
                            const parsedTags = parsePGNTags(pgnLines);

                            const fenLine = pgnLines.find(line => line.includes('[FEN '));
                            const customFen = fenLine ? extractPGNTag(fenLine, 'FEN') : null;
                            setStartingFen(customFen);

                            try {
                                const chess = new Chess();
                                if (customFen) chess.load(customFen);
                                const pgnString = pgnLines.join('\n');
                                chess.loadPgn(pgnString);
                                setGameMoves(chess.history({ verbose: true }));
                            } catch (e) {
                                setGameMoves([]);
                            }

                            setGameData({
                                pgn: pgnLines,
                                ...parsedTags
                            });
                            setErrorMessage("");
                        })
                        .catch((error) => {
                            console.error('Error fetching game:', error);
                            setErrorMessage(error.response?.data || 'Failed to load game');
                        })
                );
            } catch (error) {
                console.error('Error in fetchGame:', error);
                setErrorMessage('An unexpected error occurred');
            }
        }

        fetchGame();
    }, [gameId, parsePGNTags]);

    useEffect(() => {
        if (enableAnalysis && currentFen && isReady) {
            if (lastAnalyzedRef.current.fen !== currentFen) {
                lastAnalyzedRef.current = { fen: currentFen, depth: analysisDepthSetting };
                analyzePosition(currentFen, analysisDepthSetting);
            }
        } else if (!enableAnalysis) {
            stopAnalysis();
            lastAnalyzedRef.current = { fen: null, depth: null };
        }
    }, [enableAnalysis, currentFen, isReady, analyzePosition, stopAnalysis]);

    useEffect(() => {
        if (setMultiPV && isReady) {
            setMultiPV(numberOfLines);
        }
    }, [numberOfLines, setMultiPV, isReady]);

    const handlePositionChange = useCallback((fen, orientation) => {
        setCurrentFen(fen);
        setBoardOrientation(orientation || 'white');
    }, []);

    const handleTopMoveClick = useCallback((move) => {
        if (!move || !currentFen) return;

        const chess = new Chess(currentFen);
        const from = move.slice(0, 2);
        const to = move.slice(2, 4);
        const promotion = move.length > 4 ? move.slice(4) : undefined;

        try {
            const moveResult = chess.move({ from, to, promotion });
            if (moveResult) {
                const pgnString = gameData.pgn.join('\n');
                const gameChess = new Chess();

                if (startingFen) {
                    gameChess.load(startingFen);
                }

                gameChess.loadPgn(pgnString);
                const gameMovesHistory = gameChess.history({ verbose: true });

                const currentGameMove = gameMovesHistory[currentMoveIndex];

                const isDifferent = !currentGameMove ||
                    currentGameMove.from !== from ||
                    currentGameMove.to !== to;

                if (isDifferent) {
                    setCurrentPath(prev => [...prev, { move: moveResult, fen: chess.fen(), variations: {} }]);

                    if (currentPath.length === 0) {
                        const currentVars = variations[currentMoveIndex] || [];

                        const variationExists = currentVars.some(v =>
                            v.length > 0 &&
                            v[0].move.from === moveResult.from &&
                            v[0].move.to === moveResult.to
                        );

                        if (!variationExists) {
                            const newVariation = [{ move: moveResult, fen: chess.fen(), variations: {} }];
                            setVariations(prev => ({
                                ...prev,
                                [currentMoveIndex]: [...currentVars, newVariation]
                            }));
                        }
                    }
                } else {
                    setCurrentMoveIndex(currentMoveIndex + 1);
                    setCurrentPath([]);
                }
            }
        } catch (error) {
            console.error('Invalid move:', error);
        }
    }, [currentFen, gameData.pgn, currentMoveIndex, variations, currentPath, startingFen]);

    const handleFlipBoard = useCallback(() => {
        setBoardOrientation(prev => prev === 'white' ? 'black' : 'white');
    }, []);

    const handleToggleAnalysis = useCallback(() => {
        setEnableAnalysis(prev => !prev);
    }, []);

    const handleDepthChange = useCallback((event, newValue) => {
        setAnalysisDepthSetting(newValue);
    }, []);

    const handleDepthChangeCommitted = useCallback((event, newValue) => {
        setAnalysisDepthSetting(newValue);
        if (enableAnalysis && currentFen && isReady) {
            lastAnalyzedRef.current = { fen: currentFen, depth: newValue };
            analyzePosition(currentFen, newValue);
        }
    }, [enableAnalysis, currentFen, isReady, analyzePosition]);

    const handleLinesChange = useCallback((event, newValue) => {
        setNumberOfLines(newValue);
    }, []);

    const boardSize = useMemo(() => {
        if (isMobile) {
            const availableWidth = dimensions.width - 8;
            return Math.min(availableWidth, 400);
        }

        const headerSpace = 300;
        const sideSpace = enableAnalysis ? 440 : 380;

        const availableWidth = (dimensions.width * 0.8) - sideSpace;
        const availableHeight = (dimensions.height * 0.8) - headerSpace;

        const calculatedSize = Math.min(availableWidth, availableHeight);
        return Math.max(350, Math.min(calculatedSize, 650));
    }, [dimensions, enableAnalysis, isMobile]);

    const pgnMoves = useMemo(() => {
        if (!gameData.pgn) return "";
        return gameData.pgn
            .filter(line => !line.startsWith("[") && line.trim() !== "")
            .join("\n");
    }, [gameData.pgn]);

    const shouldFilterTag = useCallback((line) => {
        if (!line.startsWith('[')) return false;

        const tagMatch = line.match(/^\[(\w+)\s/);
        if (!tagMatch) return false;

        const tagName = tagMatch[1].toLowerCase();
        return FILTERED_PGN_TAGS.includes(tagName);
    }, []);

    const buildExportPgnString = useCallback(() => {
        if (!gameData.pgn) return "";

        const chess = new Chess();
        let pgnOutput = '';

        let hasFEN = false;
        let hasSetUp = false;

        gameData.pgn.forEach(line => {
            if (line.startsWith('[')) {
                if (shouldFilterTag(line)) {
                    return;
                }

                pgnOutput += line + '\n';
                if (line.includes('[FEN ')) hasFEN = true;
                if (line.includes('[SetUp ')) hasSetUp = true;
            }
        });

        if (startingFen && !hasFEN) {
            pgnOutput += `[FEN "${startingFen}"]\n`;
            if (!hasSetUp) {
                pgnOutput += `[SetUp "1"]\n`;
            }
        }

        pgnOutput += '\n';

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        const pgnString = gameData.pgn.join('\n');
        const tempChess = new Chess();
        tempChess.loadPgn(pgnString);
        const moves = tempChess.history({ verbose: true });

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        let moveText = '';

        const formatVariation = (variation, startIndex) => {
            let varText = '';
            variation.forEach((pathMove, idx) => {
                const moveNum = Math.floor((startIndex + idx) / 2) + 1;
                const isWhite = (startIndex + idx) % 2 === 0;

                if (isWhite) {
                    varText += `${moveNum}. `;
                } else if (idx === 0) {
                    varText += `${moveNum}... `;
                }

                varText += pathMove.move.san + ' ';
            });
            return varText.trim();
        };

        for (let i = 0; i < moves.length; i++) {
            const move = moves[i];
            const moveNumber = Math.floor(i / 2) + 1;
            const isWhiteMove = i % 2 === 0;

            if (isWhiteMove) {
                moveText += `${moveNumber}. `;
            }

            moveText += move.san + ' ';

            if (variations[i]) {
                variations[i].forEach((variation) => {
                    moveText += `(${formatVariation(variation, i)}) `;
                });
            }

            if (i === currentMoveIndex - 1 && currentPath.length > 0 && activeVariationIndex === null) {
                moveText += `(${formatVariation(currentPath, currentMoveIndex)}) `;
            }
        }

        const resultMatch = gameData.pgn.find(line => line.includes('[Result'));
        if (resultMatch) {
            const result = resultMatch.match(/"([^"]+)"/)?.[1] || '*';
            moveText += result;
        } else {
            moveText += '*';
        }

        pgnOutput += moveText;
        return pgnOutput;
    }, [gameData.pgn, currentPath, currentMoveIndex, variations, activeVariationIndex, startingFen, shouldFilterTag]);

    const exportPGN = useCallback(() => {
        const pgnOutput = buildExportPgnString();
        if (!pgnOutput) return;

        const blob = new Blob([pgnOutput], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analysis_${Date.now()}.pgn`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [buildExportPgnString]);

    const emailPGN = useCallback(async () => {
        const pgnOutput = buildExportPgnString();
        if (!pgnOutput) {
            setEmailDialogText("Error: No PGN to email.");
            setEmailDialogOpen(true);
            return;
        }

        if (isSendingEmail) return;

        setIsSendingEmail(true);
        setEmailDialogText("Sending email...");
        setEmailDialogOpen(true);

        try {
            await new Promise(resolve => setTimeout(resolve, 3000));

            const token = await authService.getAccessToken();
            const res = await fetch("/api/chessgames/EmailAnalysis", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    pgn: pgnOutput,
                    chapterName: gameData.event || "Analysis",
                    bcc: "metadbchess@gmail.com"
                })
            });

            if (!res.ok) {
                const errorText = await res.text();
                setEmailDialogText(`Failed to send email: ${errorText || res.statusText}`);
                return;
            }

            const data = await res.json();
            setEmailDialogText(data.message || "Game analysis email sent successfully!");
        } catch (err) {
            setEmailDialogText(`Failed to send email: ${err.message}`);
        } finally {
            setIsSendingEmail(false);
        }
    }, [buildExportPgnString, gameData.event, isSendingEmail]);

    const centerDivStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '20vh'
    };

    const topMovesPanelElement = enableAnalysis ? (
        <TopMovesPanel
            topMoves={topMoves || []}
            onMoveClick={handleTopMoveClick}
            currentFen={currentFen}
            isMobile={isMobile}
        />
    ) : null;

    if (promiseInProgress) {
        return (
            <Box sx={{ pt: 2, pb: 1 }}>
                <div style={centerDivStyle}>
                    <MagnifyingGlass ariaLabel="Loading" color="#00BFFF" />
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 2, pb: 1, px: 0, overflow: 'hidden', width: '100%' }}>
            {gameData.pgn && (
                <Grid container spacing={isMobile ? 1 : 2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant={isMobile ? "body2" : "h6"} align="center" component="div" sx={{ px: 1 }}>
                            <PlayerLink
                                name={gameData.white}
                                uscfId={gameData.whiteUscfId}
                                fideId={gameData.whiteFideId}
                                elo={gameData.whiteElo}
                            />
                            {' '}
                            {gameData.result}
                            {' '}
                            <PlayerLink
                                name={gameData.black}
                                uscfId={gameData.blackUscfId}
                                fideId={gameData.blackFideId}
                                elo={gameData.blackElo}
                            />
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant={isMobile ? "caption" : "subtitle1"} align="center" component="div" sx={{ px: 1 }}>
                            <EventLink
                                eventName={gameData.event}
                                uscfId={gameData.uscfEventId}
                                fideId={gameData.fideEventId}
                                date={gameData.date}
                            />
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ px: 1 }}>
                            {isAdmin && displayEditButton && (
                                <Tooltip title="Edit Data">
                                    <Button
                                        variant="contained"
                                        component={Link}
                                        to={`/Games/EditGameById/${gameId}`}
                                        color="primary"
                                        size="small"
                                        sx={{ height: '32px', minWidth: isMobile ? '36px' : 'auto', px: isMobile ? 0.5 : 2 }}
                                    >
                                        <EditIcon fontSize="small" />
                                        {!isMobile && <Box component="span" sx={{ ml: 0.5 }}>Edit</Box>}
                                    </Button>
                                </Tooltip>
                            )}

                            <Tooltip title="Export PGN">
                                <Button
                                    variant="contained"
                                    onClick={exportPGN}
                                    color="primary"
                                    size="small"
                                    sx={{ height: '32px', minWidth: isMobile ? '36px' : 'auto', px: isMobile ? 0.5 : 2 }}
                                >
                                    <DownloadIcon fontSize="small" />
                                    {!isMobile && <Box component="span" sx={{ ml: 0.5 }}>Export</Box>}
                                </Button>
                            </Tooltip>

                            <Tooltip title="Email PGN">
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={emailPGN}
                                        color="secondary"
                                        size="small"
                                        sx={{ height: '32px', minWidth: isMobile ? '36px' : 'auto', px: isMobile ? 0.5 : 2 }}
                                        disabled={isSendingEmail}
                                    >
                                        <EmailIcon fontSize="small" />
                                        {!isMobile && <Box component="span" sx={{ ml: 0.5 }}>{isSendingEmail ? 'Sending...' : 'Email'}</Box>}
                                    </Button>
                                </span>
                            </Tooltip>

                            {isAdmin && (
                                <AnalyzeOnLichess
                                    studyId="T9fzisoU"
                                    chapterName={gameData.event + " " + gameData.white + " vs " + gameData.black}
                                    apiToken={process.env.REACT_APP_LICHESS_API_TOKEN || "lip_1bQ56NtA8QuhUJU6oVFo"}
                                    pgn={pgnMoves}
                                    isMobile={isMobile}
                                />
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="flex-start" gap={isMobile ? 0 : 2}>
                        {!isMobile && enableAnalysis && (
                            <Box sx={{ display: 'flex', alignItems: 'stretch', height: boardSize }}>
                                <EvaluationBar
                                    evaluation={evaluation}
                                    isAnalyzing={isAnalyzing}
                                    orientation={boardOrientation}
                                    analysisDepth={analysisDepth}
                                />
                            </Box>
                        )}
                        <Box sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: isMobile ? '100%' : 'auto',
                            px: isMobile ? 0 : 0  // Remove any horizontal padding on mobile
                        }}>
                            {isMobile && enableAnalysis && (
                                <MobileEvaluationOverlay
                                    evaluation={evaluation}
                                    isAnalyzing={isAnalyzing}
                                    boardSize={boardSize}
                                    currentMoveIndex={currentMoveIndex}
                                    gameMoves={gameMoves}
                                    bestMove={bestMove}
                                    currentFen={currentFen}
                                />
                            )}
                            <PGNViewerWithAnalysis
                                pgn={gameData.pgn}
                                size={boardSize}
                                onPositionChange={handlePositionChange}
                                currentPath={currentPath}
                                setCurrentPath={setCurrentPath}
                                currentMoveIndex={currentMoveIndex}
                                setCurrentMoveIndex={setCurrentMoveIndex}
                                boardOrientation={boardOrientation}
                                variations={variations}
                                setVariations={setVariations}
                                activeVariationIndex={activeVariationIndex}
                                setActiveVariationIndex={setActiveVariationIndex}
                                isMobile={isMobile}
                                topMovesPanel={topMovesPanelElement}
                                // New props for integrated controls
                                onFlipBoard={handleFlipBoard}
                                onToggleAnalysis={handleToggleAnalysis}
                                isAnalysisEnabled={enableAnalysis}
                                isStockfishReady={isReady}
                                analysisDepth={analysisDepthSetting}
                                onDepthChange={handleDepthChange}
                                onDepthChangeCommitted={handleDepthChangeCommitted}
                                numberOfLines={numberOfLines}
                                onLinesChange={handleLinesChange}
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}

            {errorMessage && (
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
                            {errorMessage}
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <SupportUs />
                    </Grid>
                </Grid>
            )}

            <Dialog open={emailDialogOpen} onClose={() => !isSendingEmail && setEmailDialogOpen(false)}>
                <DialogTitle>Email PGN</DialogTitle>
                <DialogContent>
                    <DialogContentText>{emailDialogText}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)} disabled={isSendingEmail}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}